import type { ApiClientDeps } from '@pay-roadrunner/api-client-core';
import { parseOrThrow } from '@pay-roadrunner/api-client-core';
import type { AuditEventListPage, AuditLogQuery } from './auditLogApiTypes';

/** PRR-430 Administration -> Audit Log read client. Both apps' `/api/audit-events` endpoints share this exact query/response contract. */
export const createAuditLogClient = (adapter: ApiClientDeps) => {
  const fetchImpl = adapter.fetchImpl ?? fetch;
  const base = () => `${adapter.getBaseUrl()}/api/audit-events`;

  const listAuditEvents = async (query: AuditLogQuery, signal?: AbortSignal): Promise<AuditEventListPage> => {
    const params = new URLSearchParams({
      page: String(query.page),
      pageSize: String(query.pageSize),
      sortBy: query.sortBy,
      sortDescending: String(query.sortDescending),
    });
    if (query.search?.trim()) params.set('search', query.search.trim());
    if (query.userId?.trim()) params.set('userId', query.userId.trim());
    if (query.personId?.trim()) params.set('personId', query.personId.trim());
    if (query.userEmail?.trim()) params.set('userEmail', query.userEmail.trim());
    if (query.tenantSlug?.trim()) params.set('tenantSlug', query.tenantSlug.trim());
    if (query.eventName?.trim()) params.set('eventName', query.eventName.trim());
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);

    const response = await parseOrThrow<AuditEventListPage>(await fetchImpl(`${base()}?${params.toString()}`, { signal }));
    return response.result ?? { items: [], page: query.page, pageSize: query.pageSize, totalCount: 0, totalPages: 0 };
  };

  const listEventNames = async (signal?: AbortSignal): Promise<string[]> => {
    const response = await parseOrThrow<string[]>(await fetchImpl(`${base()}/event-names`, { signal }));
    return response.result ?? [];
  };

  return { listAuditEvents, listEventNames };
};

export type AuditLogClient = ReturnType<typeof createAuditLogClient>;
